import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from 'react';
import axios from '../utils/Axios';
import { setSession } from './utils';
// @ts-ignore
import { themeEventEmitter } from 'src/theme';

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const Types = {
  INITIAL: 'INITIAL',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
};

const LOGOUT_WAIT_MS = 2000;

const reducer = (state, action) => {
  if (action.type === Types.INITIAL) {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      isNewUser: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      isNewUser: action.payload.isNewUser,
      isAuthenticated: true,
      user: null,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      isAuthenticated: false,
      isNewUser: false,
      user: null,
    };
  }
  return state;
};

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem('token');
      const userId = localStorage.getItem('user');
      if (accessToken && userId) {
        setSession(accessToken, userId);

        const response = await axios.get('/user');
        const { user } = response.data;

        if (user) {
          if (user.theme && user.theme.primaryColor && user.theme.accentColor) {
            localStorage.setItem('primaryColor', user.theme.primaryColor);
            localStorage.setItem('accentColor', user.theme.accentColor);
            localStorage.setItem('logo', user.theme.logo);

            themeEventEmitter.emit('updateTheme');
          }
        }

        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        });
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const verifyGoogleLogin = useCallback(async (code) => {
    const response = await axios.post('/auth/google', {
      code,
    });

    console.log(response);

    const { token, user, isNewUser, error } = response.data;

    if (error) {
      window.location = '/login?error=auth';
      return;
    }

    setSession(token, user);
    dispatch({
      type: Types.LOGIN,
      payload: {
        isNewUser,
        isAuthenticated: true,
      },
    });

    setTimeout(() => {
      window.location = '/dashboard/app';
    }, 100);
  }, []);

  const loginUserWithPassword = useCallback(async (data) => {
    const response = await axios.post('/auth/login', data);
    const { token, user, isNewUser, error } = response.data;

    if (error) {
      throw new Error(error);
    }

    setSession(token, user);
    dispatch({
      type: Types.LOGIN,
      payload: {
        isNewUser,
        isAuthenticated: true,
      },
    });
  }, []);

  const signUpUser = useCallback(async (data) => {
    const response = await axios.post('/auth/register', data);
    const { token, user, isNewUser, error } = response.data;

    if (error) {
      throw new Error(error);
    }

    setSession(token, user);
    dispatch({
      type: Types.LOGIN,
      payload: {
        isNewUser,
        isAuthenticated: true,
      },
    });
  }, []);

  // The server ends the token first, while the headers still hold it. Logout waits for
  // the answer, because AuthGuard then loads the login page and that would cancel a call
  // still in flight. The wait is capped, and every status counts as an answer, so a 404
  // from a backend without the route or a 401 never reaches the forced logout in
  // utils/Axios.js. Whatever the call does, the browser forgets the session.
  const logout = useCallback(async () => {
    await axios
      .post('/auth/logout', null, { timeout: LOGOUT_WAIT_MS, validateStatus: () => true })
      .catch(() => {});
    setSession(null, null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      const response = await axios.get('/user/onboarding-status');
      return response.data;
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      return { onboardingCompleted: false };
    }
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      isNewUser: state.isNewUser,
      user: state.user,
      setGlobalUser: (user) =>
        dispatch({
          type: Types.INITIAL,
          payload: {
            isAuthenticated: true,
            user,
          },
        }),
      signUpUser,
      loginUserWithPassword,
      verifyGoogleLogin,
      logout,
    }),
    [
      state.isAuthenticated,
      state.isNewUser,
      state.isInitialized,
      state.user,
      signUpUser,
      loginUserWithPassword,
      verifyGoogleLogin,
      logout,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
